import {
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import type {
  CollaborationActor,
  CollaborationRelationship,
  CollaborationResponse,
} from "@insights/contracts";
import { Card, SegmentedControl } from "@insights/ui";

type InteractionView = "matrix" | "network";

const interactionViews = [
  { value: "matrix", label: "Matrix" },
  { value: "network", label: "Network" },
] as const;

function relationshipKey(relationship: CollaborationRelationship) {
  return `${relationship.reviewer.toLowerCase()}\u0000${relationship.author.toLowerCase()}`;
}

function strengthClass(percent: number) {
  if (percent >= 75) return "team-interaction-cell--strong";
  if (percent >= 50) return "team-interaction-cell--medium";
  if (percent >= 25) return "team-interaction-cell--light";
  return "team-interaction-cell--faint";
}

function RelationshipDetail({
  relationship,
}: {
  relationship: CollaborationRelationship | null;
}) {
  if (!relationship) {
    return (
      <p className="team-interaction-selection">
        Select a relationship to inspect its pull requests.
      </p>
    );
  }

  return (
    <div className="team-interaction-detail" aria-live="polite">
      <div>
        <strong>{relationship.reviewer} reviewed {relationship.author}</strong>
        <span>
          {relationship.pullRequestCount} pull request{relationship.pullRequestCount === 1 ? "" : "s"}
          {" · "}{relationship.authorCoveragePercent}% of {relationship.author}&apos;s pull requests
        </span>
      </div>
      <ul>
        {relationship.pullRequests.map((pullRequest) => (
          <li key={`${pullRequest.repository}:${pullRequest.number}`}>
            {pullRequest.url ? (
              <a href={pullRequest.url} rel="noreferrer" target="_blank">
                {pullRequest.repository}#{pullRequest.number} {pullRequest.title}
              </a>
            ) : (
              <span>
                {pullRequest.repository}#{pullRequest.number} {pullRequest.title}
              </span>
            )}
            {pullRequest.reviewStates.length > 0 && (
              <small>{pullRequest.reviewStates.join(", ").toLowerCase()}</small>
            )}
          </li>
        ))}
      </ul>
      {relationship.pullRequestCount > relationship.pullRequests.length && (
        <small className="team-interaction-evidence-limit">
          Showing the {relationship.pullRequests.length} most recent pull requests.
        </small>
      )}
    </div>
  );
}

function InteractionMatrix({
  data,
  selected,
  onSelect,
}: {
  data: CollaborationResponse;
  selected: CollaborationRelationship | null;
  onSelect: (relationship: CollaborationRelationship) => void;
}) {
  const relationships = new Map(
    data.relationships.map((relationship) => [
      relationshipKey(relationship),
      relationship,
    ]),
  );
  const authors = data.actors.filter((actor) => actor.authoredPullRequests > 0);

  return (
    <div className="team-interaction-matrix-scroll">
      <table className="team-interaction-matrix">
        <caption className="ui-visually-hidden">
          Pull request authors by reviewers. Cells show the percentage of each author&apos;s pull requests reviewed by that person.
        </caption>
        <thead>
          <tr>
            <th scope="col">Author</th>
            {data.actors.map((reviewer) => (
              <th key={reviewer.login} scope="col">{reviewer.login}</th>
            ))}
            <th scope="col">Peer review coverage</th>
          </tr>
        </thead>
        <tbody>
          {authors.map((author) => (
            <tr key={author.login}>
              <th scope="row">
                {author.login}
                <small>{author.authoredPullRequests} PR{author.authoredPullRequests === 1 ? "" : "s"}</small>
              </th>
              {data.actors.map((reviewer) => {
                const isSelf = author.login.toLowerCase() === reviewer.login.toLowerCase();
                const relationship = relationships.get(
                  `${reviewer.login.toLowerCase()}\u0000${author.login.toLowerCase()}`,
                );
                if (isSelf) {
                  return <td className="team-interaction-self" key={reviewer.login}>—</td>;
                }
                if (!relationship) {
                  return <td key={reviewer.login}><span className="team-interaction-none">0</span></td>;
                }
                return (
                  <td key={reviewer.login}>
                    <button
                      aria-label={`${reviewer.login} reviewed ${relationship.pullRequestCount} of ${author.login}'s pull requests`}
                      className={`team-interaction-cell ${strengthClass(relationship.authorCoveragePercent)}${selected && relationshipKey(selected) === relationshipKey(relationship) ? " team-interaction-cell--selected" : ""}`}
                      onClick={() => onSelect(relationship)}
                      type="button"
                    >
                      <strong>{relationship.authorCoveragePercent}%</strong>
                      <small>{relationship.pullRequestCount} PR{relationship.pullRequestCount === 1 ? "" : "s"}</small>
                    </button>
                  </td>
                );
              })}
              <td className="team-interaction-coverage">
                {author.reviewCoveragePercent === null ? "—" : `${author.reviewCoveragePercent}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function actorActivity(actor: CollaborationActor) {
  return actor.authoredPullRequests + actor.reviewedPullRequests;
}

function InteractionNetwork({
  data,
  selected,
  onSelect,
}: {
  data: CollaborationResponse;
  selected: CollaborationRelationship | null;
  onSelect: (relationship: CollaborationRelationship) => void;
}) {
  const markerId = useId().replaceAll(":", "");
  const actors = useMemo(
    () => [...data.actors]
      .sort((left, right) => actorActivity(right) - actorActivity(left))
      .slice(0, 12),
    [data.actors],
  );
  const included = new Set(actors.map((actor) => actor.login.toLowerCase()));
  const relationships = data.relationships.filter((relationship) =>
    included.has(relationship.author.toLowerCase())
    && included.has(relationship.reviewer.toLowerCase()));
  const positions = new Map(actors.map((actor, index) => {
    const angle = (Math.PI * 2 * index) / actors.length - Math.PI / 2;
    return [actor.login.toLowerCase(), {
      x: 400 + Math.cos(angle) * 280,
      y: 210 + Math.sin(angle) * 155,
    }] as const;
  }));
  const maximum = Math.max(1, ...relationships.map((relationship) => relationship.pullRequestCount));

  function selectFromKeyboard(
    event: KeyboardEvent<SVGGElement>,
    relationship: CollaborationRelationship,
  ) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(relationship);
    }
  }

  return (
    <div className="team-interaction-network">
      {data.actors.length > actors.length && (
        <p>Showing the 12 most active contributors to keep the network readable.</p>
      )}
      <svg
        aria-label="Directed collaboration network. Arrows point from reviewers to pull request authors."
        role="img"
        viewBox="0 0 800 420"
      >
        <defs>
          <marker
            id={markerId}
            markerHeight="6"
            markerWidth="6"
            orient="auto"
            refX="5"
            refY="3"
            viewBox="0 0 6 6"
          >
            <path d="M0,0 L6,3 L0,6 Z" />
          </marker>
        </defs>
        <g className="team-interaction-edges">
          {relationships.map((relationship) => {
            const from = positions.get(relationship.reviewer.toLowerCase())!;
            const to = positions.get(relationship.author.toLowerCase())!;
            const deltaX = to.x - from.x;
            const deltaY = to.y - from.y;
            const distance = Math.hypot(deltaX, deltaY);
            const startX = from.x + (deltaX / distance) * 31;
            const startY = from.y + (deltaY / distance) * 31;
            const endX = to.x - (deltaX / distance) * 35;
            const endY = to.y - (deltaY / distance) * 35;
            const hasReverse = relationships.some((candidate) =>
              candidate.author.toLowerCase() === relationship.reviewer.toLowerCase()
              && candidate.reviewer.toLowerCase() === relationship.author.toLowerCase());
            const curve = hasReverse ? 18 : 0;
            const controlX = (startX + endX) / 2 - (deltaY / distance) * curve;
            const controlY = (startY + endY) / 2 + (deltaX / distance) * curve;
            const path = `M${startX},${startY} Q${controlX},${controlY} ${endX},${endY}`;
            const isSelected = selected
              && relationshipKey(selected) === relationshipKey(relationship);
            return (
              <g
                aria-label={`${relationship.reviewer} reviewed ${relationship.pullRequestCount} pull requests by ${relationship.author}`}
                className={isSelected ? "is-selected" : undefined}
                key={relationshipKey(relationship)}
                onClick={() => onSelect(relationship)}
                onKeyDown={(event) => selectFromKeyboard(event, relationship)}
                role="button"
                tabIndex={0}
              >
                <path
                  className="team-interaction-edge-hit"
                  d={path}
                />
                <path
                  d={path}
                  fill="none"
                  markerEnd={`url(#${markerId})`}
                  strokeWidth={1.5 + (relationship.pullRequestCount / maximum) * 5}
                />
              </g>
            );
          })}
        </g>
        <g className="team-interaction-nodes">
          {actors.map((actor) => {
            const position = positions.get(actor.login.toLowerCase())!;
            const label = actor.login.length > 14
              ? `${actor.login.slice(0, 12)}…`
              : actor.login;
            return (
              <g key={actor.login} transform={`translate(${position.x} ${position.y})`}>
                <circle r="27" />
                <text textAnchor="middle" y="4">{label}</text>
                <title>
                  {actor.login}: authored {actor.authoredPullRequests}, reviewed {actor.reviewedPullRequests} pull requests
                </title>
              </g>
            );
          })}
        </g>
      </svg>
      <p className="team-interaction-network-legend">
        Arrows point from reviewer to author. Thicker arrows represent more distinct pull requests.
      </p>
    </div>
  );
}

export function TeamInteractions({ data }: { data: CollaborationResponse }) {
  const [view, setView] = useState<InteractionView>("matrix");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = data.relationships.find(
    (relationship) => relationshipKey(relationship) === selectedKey,
  ) ?? null;

  useEffect(() => {
    if (selectedKey && !selected) setSelectedKey(null);
  }, [selected, selectedKey]);

  const coverage = data.pullRequestCount
    ? Math.round((data.peerReviewedPullRequestCount / data.pullRequestCount) * 100)
    : null;

  return (
    <Card as="section" className="team-interactions">
      <header>
        <div>
          <span className="team-interactions-eyebrow">Team interactions · last 90 days</span>
          <h2>Who reviews whom</h2>
          <p>
            {data.peerReviewedPullRequestCount} of {data.pullRequestCount} pull requests received a review from another contributor
            {coverage === null ? "." : ` (${coverage}%).`}
          </p>
        </div>
        <SegmentedControl
          label="Interaction view"
          name="interaction-view"
          onValueChange={setView}
          options={interactionViews}
          value={view}
        />
      </header>
      <p className="team-interactions-scope">{data.scopeNote}</p>
      {data.actors.length < 2 || data.relationships.length === 0 ? (
        <div className="team-interactions-empty">
          Not enough peer-review activity is available for this scope yet.
        </div>
      ) : (
        <>
          {view === "matrix" ? (
            <InteractionMatrix
              data={data}
              onSelect={(relationship) => setSelectedKey(relationshipKey(relationship))}
              selected={selected}
            />
          ) : (
            <InteractionNetwork
              data={data}
              onSelect={(relationship) => setSelectedKey(relationshipKey(relationship))}
              selected={selected}
            />
          )}
          <RelationshipDetail relationship={selected} />
        </>
      )}
    </Card>
  );
}
